
import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Gamepad, Rocket, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { usePageTitle } from "@/hooks/usePageTitle";

const NotFound = () => {
  usePageTitle("Страница не найдена");
  const location = useLocation();
  const [score, setScore] = useState(0);
  const [isGameActive, setIsGameActive] = useState(false);

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  const handleStartGame = () => {
    setIsGameActive(true);
    setScore(0);
  };

  const handleScorePoint = () => {
    setScore((prevScore) => prevScore + 10);
  };

  const handleReturnHome = () => {
    window.location.href = "/";
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-primary/5 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-xl bg-white/80 backdrop-blur-lg rounded-2xl p-8 border border-primary/20 shadow-xl relative overflow-hidden"
      >
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 h-32 w-32 bg-primary/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl"></div>
        <div className="absolute bottom-0 left-0 h-24 w-24 bg-accent/10 rounded-full translate-y-1/2 -translate-x-1/2 blur-2xl"></div>
        
        {/* Main content */}
        <div className="text-center relative z-10">
          {/* 404 Number */}
          <div className="font-display text-[120px] leading-none font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent mb-6">
            404
          </div>
          
          {!isGameActive ? (
            <>
              <h1 className="text-2xl font-bold mb-4">
                Упс! Страница не найдена
              </h1>
              <p className="text-muted-foreground mb-8">
                Страница, которую вы ищете, не существует или была перемещена.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={handleStartGame}
                  className="flex items-center gap-2"
                  size="lg"
                >
                  <Gamepad className="size-5" />
                  Поиграть
                </Button>
                <Button 
                  onClick={handleReturnHome}
                  variant="outline" 
                  className="flex items-center gap-2"
                  size="lg"
                >
                  <ArrowLeft className="size-5" />
                  Вернуться на главную
                </Button>
              </div>
            </>
          ) : (
            <GameComponent 
              score={score} 
              onScore={handleScorePoint} 
              onExit={() => setIsGameActive(false)} 
            />
          )}
        </div>
      </motion.div>
    </div>
  );
};

// Simple mini-game component
const GameComponent = ({ score, onScore, onExit }: { 
  score: number; 
  onScore: () => void; 
  onExit: () => void 
}) => {
  const [rocketPosition, setRocketPosition] = useState({ x: 50, y: 50 });
  const [targets, setTargets] = useState<Array<{id: number, x: number, y: number}>>([
    { id: 1, x: 20, y: 20 },
    { id: 2, x: 70, y: 30 },
    { id: 3, x: 30, y: 70 }
  ]);

  // Move rocket with mouse
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const gameArea = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - gameArea.left) / gameArea.width) * 100;
    const y = ((e.clientY - gameArea.top) / gameArea.height) * 100;
    
    setRocketPosition({
      x: Math.min(Math.max(x, 5), 95),
      y: Math.min(Math.max(y, 5), 95)
    });
  };

  // Check for target hits
  useEffect(() => {
    const hitDistance = 15;
    
    targets.forEach(target => {
      const dx = rocketPosition.x - target.x;
      const dy = rocketPosition.y - target.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < hitDistance) {
        onScore();
        setTargets(prev => {
          // Remove the hit target
          const filtered = prev.filter(t => t.id !== target.id);
          // Add a new target at random position
          const newTarget = { 
            id: target.id + prev.length, 
            x: 5 + Math.random() * 90, 
            y: 5 + Math.random() * 90 
          };
          return [...filtered, newTarget];
        });
      }
    });
  }, [rocketPosition, targets, onScore]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Собери звёзды</h2>
        <div className="text-xl font-semibold">Счёт: {score}</div>
      </div>
      
      {/* Game area */}
      <div 
        className="relative bg-gradient-to-br from-primary/5 to-accent/5 border-2 border-dashed border-primary/20 rounded-lg h-64 overflow-hidden"
        onMouseMove={handleMouseMove}
      >
        {/* Rocket (player) */}
        <motion.div 
          className="absolute"
          animate={{
            left: `${rocketPosition.x}%`,
            top: `${rocketPosition.y}%`,
            transform: 'translate(-50%, -50%)'
          }}
          transition={{
            type: "spring",
            stiffness: 200,
            damping: 20
          }}
        >
          <Rocket className="h-10 w-10 text-primary animate-float" />
        </motion.div>
        
        {/* Targets */}
        {targets.map(target => (
          <motion.div
            key={target.id}
            className="absolute"
            style={{
              left: `${target.x}%`,
              top: `${target.y}%`,
              transform: 'translate(-50%, -50%)'
            }}
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            <div className="h-6 w-6 bg-accent rounded-full flex items-center justify-center">
              <div className="h-3 w-3 bg-white rounded-full"></div>
            </div>
          </motion.div>
        ))}
      </div>
      
      <div className="text-center text-sm text-muted-foreground">
        Наведите курсор на точки, чтобы набрать очки
      </div>
      
      <div className="flex justify-center">
        <Button 
          onClick={onExit}
          variant="outline"
          className="flex items-center gap-2"
        >
          Закончить игру
        </Button>
      </div>
    </div>
  );
};

export default NotFound;